// The registry of website templates (§3, §5.3). Manifests are eager and tiny;
// a template's module is reached through `manifest.module` (classic, eager)
// or `manifest.load()` (everything else, lazy).
//
// Folders starting with `_` (the `_skeleton` starter) are never registered.

import type { SiteTemplateManifest } from './types'

// The negative pattern keeps `_`-prefixed starters out of the bundle entirely.
const modules = import.meta.glob<{ default: SiteTemplateManifest }>(
  ['../../templates/*/manifest.ts', '!../../templates/_*/manifest.ts'],
  { eager: true },
)

/** Folder name of a manifest module path ('../../templates/arena/manifest.ts' → 'arena'). */
export function folderOf(path: string): string {
  const m = /templates\/([^/]+)\/manifest\.ts$/.exec(path)
  return m ? m[1] : ''
}

function buildRegistry(): Record<string, SiteTemplateManifest> {
  const out: Record<string, SiteTemplateManifest> = {}
  for (const [path, mod] of Object.entries(modules)) {
    const folder = folderOf(path)
    if (!folder || folder.startsWith('_')) continue
    out[folder] = mod.default
  }
  return out
}

/** Registered manifests by folder (= id, enforced by the manifest contract test). */
export const SITE_TEMPLATE_MANIFESTS: Readonly<Record<string, SiteTemplateManifest>> = buildRegistry()

export const DEFAULT_SITE_TEMPLATE_ID = 'classic'

/**
 * Manifests in picker order: classic first, then stable, then beta, each by
 * name. Eager and template-code-free (non-classic manifests only hold a
 * `load()` thunk), so the admin picker can import this without pulling any
 * template chunk.
 */
export function listSiteTemplateManifests(): SiteTemplateManifest[] {
  const rank = (m: SiteTemplateManifest) => (m.id === DEFAULT_SITE_TEMPLATE_ID ? 0 : m.status === 'stable' ? 1 : 2)
  return Object.values(SITE_TEMPLATE_MANIFESTS).sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
}

export function getManifest(id: string | null | undefined): SiteTemplateManifest | null {
  if (!id) return null
  return Object.prototype.hasOwnProperty.call(SITE_TEMPLATE_MANIFESTS, id) ? SITE_TEMPLATE_MANIFESTS[id] : null
}
